import { array, either, option, readonlyArray, string } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { ModalComponent } from "../components/modal/modal.component";
import { modal } from "tingle.js";
import { Failure } from "../helpers/failure";
import {
  FragmentParser,
  NavigationPath,
} from "../components/parsers/fragmentParser";
import { BreadcrumbIcons } from "../models/searchView";
import { destroySwiper } from "../helpers/swiper-helpers";

export type NavigationIdentity = { name: string; slug: string };

export interface INavigationService {
  back(): void;
  openItem(
    content: string,
    identity: NavigationIdentity,
    clientSpecificClass: string
  ): void;
  getContent(): HTMLElement;
  readStoredState(): NavigationPath[];
}

export class ModalNavigationService implements INavigationService {
  container: HTMLElement;
  constructor(initialElement: HTMLElement) {
    this.container = initialElement;
  }
  readStoredState(): NavigationPath[] {
    return [];
  }
  private modalStack: modal[] = [];
  getContent(): HTMLElement {
    return pipe(
      array.last(this.modalStack),
      option.map((modal) => modal.getContent()),
      option.getOrElseW(() => this.container)
    );
  }
  back(): void {
    destroySwiper();
    const modal = this.modalStack.pop();
    modal?.close();
  }
  openItem(
    content: string,
    identity: NavigationIdentity,
    clientSpecificClass: string
  ) {
    const beforeClose = () => {
      this.back();
      return true;
    };

    const modal = ModalComponent.openModalWithUrlState(
      content,
      clientSpecificClass,
      identity.slug,
      beforeClose
    );
    modal.getContent();
    this.modalStack.push(modal);
  }
}

type Page = {
  element: HTMLElement;
  identity: NavigationIdentity;
  scrollPosition: number;
};
export class AppNavigationService implements INavigationService {
  private breadcrumbElement: HTMLElement;
  private appElement: HTMLElement;
  private pageStack: Page[] = [];
  private formId: number;
  private breadcrumbIcons: BreadcrumbIcons;

  constructor(
    appElement: HTMLElement,
    formId: number,
    breadcrumbIcons: BreadcrumbIcons
  ) {
    window.scrollY;

    this.appElement = appElement;
    this.breadcrumbElement = document.createElement("div");
    this.breadcrumbElement.classList.add("ug-breadcrumbs");
    const rootPage = document.createElement("div");
    rootPage.classList.add("ug-page");
    appElement.classList.add("ug");
    this.formId = formId;
    this.breadcrumbIcons = breadcrumbIcons;

    appElement.innerHTML = "";
    appElement.appendChild(this.breadcrumbElement);
    appElement.appendChild(rootPage);
    this.pageStack.push({
      element: rootPage,
      identity: {
        name: `<span class="breadcrumb-home">${this.breadcrumbIcons.home}</span><span class="breadcrumb-back">${this.breadcrumbIcons.back}</span>`,
        slug: "",
      },
      scrollPosition: 0,
    });
    this.renderBreadcrumbs();
  }
  readStoredState(): NavigationPath[] {
    return this.decodeFragment();
  }
  getContent(): HTMLElement {
    return pipe(
      array.last(this.pageStack),
      option.getOrElseW(() => Failure("No elements in navigation stack"))
    ).element;
  }
  back(): void {
    destroySwiper();

    const scrollToPostion = pipe(
      this.pageStack,
      array.last,
      option.map((page) => page.scrollPosition),
      option.getOrElse(() => 0)
    );
    const page = this.pageStack.pop();
    page?.element.remove();

    this.renderBreadcrumbs();
    window.location.hash = this.calculateFragment();

    window.scrollTo(0, scrollToPostion);
  }
  openItem(content: string, identity: NavigationIdentity) {
    const newPage = document.createElement("div");

    this.pageStack.push({
      element: newPage,
      identity: identity,
      scrollPosition: window.scrollY,
    });

    newPage.classList.add("ug-page");
    newPage.innerHTML = content;
    this.appElement.appendChild(newPage);
    this.renderBreadcrumbs();

    const appPosition = this.appElement.getBoundingClientRect();
    window.scrollTo(0, appPosition.top + window.pageYOffset - 125);

    window.location.hash = this.calculateFragment();

    newPage
      .querySelector(".modal_close")
      ?.addEventListener("click", () => this.back());

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.pageStack.length >= 2) {
        this.back();
      }
    });
  }

  private popN(n: number) {
    for (let i = 0; i < n; i++) {
      this.back();
    }
  }

  private renderBreadcrumbs() {
    const stackSize = this.pageStack.length;

    function generateBreadcrumbHtml(
      page: Page,
      index: number,
      pageStack: Page[]
    ) {
      const isLast = index === pageStack.length - 1;
      const isFirst = index === 0;
      const className = isLast
        ? "ug-crumb"
        : isFirst
        ? "ug-crumb"
        : "ug-crumb active-crumb";
      return `<span class="${className}" data-page-id="${page.identity.slug}">${page.identity.name}</span>`;
    }

    let breadcrumbHtmlString = pipe(
      this.pageStack,
      array.map((page) => {
        const index = this.pageStack.indexOf(page);
        return generateBreadcrumbHtml(page, index, this.pageStack);
      }),
      array.intercalate(string.Monoid)(
        `<span class="breadcrumb-seperator">${this.breadcrumbIcons.separator}</span>`
      )
    );

    this.breadcrumbElement.innerHTML = breadcrumbHtmlString;
    let crumbs = Array.from(
      this.breadcrumbElement.querySelectorAll(".ug-crumb")
    );
    array
      .reverse(crumbs)
      .forEach((crumb, index) =>
        crumb.addEventListener("click", () => this.popN(index))
      );
    const backButton = this.breadcrumbElement
      .querySelector(".breadcrumb-back")
      ?.addEventListener("click", (e) => {
        e.stopImmediatePropagation();
        this.back();
      });
  }

  private calculateFragment(): string {
    return pipe(
      array.tail(this.pageStack), // skip the home element
      option.getOrElse(() => [] as Page[]),
      array.reduce("", (fragment, page) => fragment + "/" + page.identity.slug),
      (fragment) => (fragment == "" ? "" : `/${this.formId}${fragment}/`)
    );
  }
  private decodeFragment(): NavigationPath[] {
    return pipe(
      window.location.hash,
      FragmentParser.parseFragments,
      either.getOrElseW(() => [])
    );
  }
}
